/**
 * MainPacingTile — quick-260626-cot Task 3.2.
 *
 * Analytical replacement for the MAIN-17 plain trend card in the Sales-tab
 * headline band. Renders a cumulative-vs-target PACING chart:
 *   - X = close month across the fiscal year (fiscal order)
 *   - cumulative ACTUAL line (cyan) up to "now"
 *   - straight TARGET reference line 0 → FY target across the span (dashed)
 *   - amber shaded GAP between actual and target while actual is below target
 *
 * Headline = current cumulative ($4.7M) + "$X behind pace" delta vs the target
 * pace at the latest actual month. Matches mockups/density-redesign-v2.html
 * `${PACE}` + `.pace-legend`. Featured/wider tile (spans 2 columns).
 *
 * Data sources (the MAIN-46 empty-tile lesson — needs BOTH):
 *   - metrics['MAIN-17']  → headline scalar + FY target (Family B single read)
 *   - grouped['MAIN-48']  → the monthly cumulative series; each bucket carries
 *                           { label, monthly, cumulative, count, fyTarget }.
 * The FY target is read from the series' carried `fyTarget` (robust — does not
 * depend on the MAIN-17 single-reader target resolution), falling back to
 * metrics['MAIN-17'].target_value, then to the bucket count.
 *
 * UX-07 4-state machine: loading / cache-warming / sf-unreachable / success —
 * mirrors MainTreemapTile so the family stays consistent.
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import { buildEchartsOption } from '../../lib/echarts-builder.js';
import { formatCurrency } from '../../lib/format.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';
import { Tooltip } from '../ui/Tooltip.js';

import type { MainGroupedResponse, MainMetricResponse } from '@saledash/shared';

interface MainPacingTileProps {
  metricId: string;
  title: string;
  tooltipFormula?: string | undefined;
  /** MAIN-17 headline scalar + FY target. */
  metric: MainMetricResponse | undefined;
  /** MAIN-48 monthly cumulative pacing series. */
  series: MainGroupedResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** A single month point parsed off the MAIN-48 bucket array. */
interface PacingPoint {
  label: string;
  cumulative: number;
  fyTarget: number;
  /**
   * quick-260630-f7f Task 6 (item 6): FY target NET of Closed-Won (= fyTarget −
   * closedWonFy). 0 on older cache rows that predate the field → the tile falls
   * back to the gross fyTarget behavior.
   */
  adjustedTarget: number;
  /** Closed-Won FY bookings already recognized. */
  closedWonFy: number;
}

/** Parse the MAIN-48 buckets into typed month points (drop malformed rows). */
function toPacingPoints(buckets: MainGroupedResponse['buckets']): PacingPoint[] {
  const points: PacingPoint[] = [];
  for (const b of buckets) {
    const label = typeof b.label === 'string' ? b.label : '';
    const cumulative = typeof b.cumulative === 'number' ? b.cumulative : 0;
    const fyTarget = typeof b.fyTarget === 'number' ? b.fyTarget : 0;
    const adjustedTarget = typeof b.adjustedTarget === 'number' ? b.adjustedTarget : 0;
    const closedWonFy = typeof b.closedWonFy === 'number' ? b.closedWonFy : 0;
    if (!label) continue;
    points.push({ label, cumulative, fyTarget, adjustedTarget, closedWonFy });
  }
  return points;
}

export function MainPacingTile({
  metricId,
  title,
  tooltipFormula,
  metric,
  series,
  isLoading,
  isError,
  error,
  refetch,
}: MainPacingTileProps): React.JSX.Element {
  const tooltipContent = tooltipFormula ? (
    <>
      <p className="mb-1 font-semibold text-ink-900">{title}</p>
      <p>{tooltipFormula}</p>
    </>
  ) : (
    <p className="font-semibold text-ink-900">{title}</p>
  );

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        {title}
      </CardTitle>
      {tooltipFormula && (
        <Tooltip trigger="?" label="Show definition">
          {tooltipContent}
        </Tooltip>
      )}
    </CardHeader>
  );

  // UX-07: cache-warming
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4">
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // UX-07: SF-unreachable
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4">
          <SfUnreachableBanner onRetry={refetch} />
        </div>
      </Card>
    );
  }

  // Loading (need the series to draw anything meaningful).
  if (isLoading || !series) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4" aria-busy="true">
          <Skeleton className="h-8 w-28" data-testid="skeleton-value" />
          <Skeleton className="mt-3 h-[150px] w-full" data-testid="skeleton-pacing" />
        </div>
      </Card>
    );
  }

  const points = toPacingPoints(series.buckets);

  // FY target: prefer the series' carried fyTarget (robust), then the MAIN-17
  // single-reader target, then 0.
  const carriedTarget = points.find((p) => p.fyTarget > 0)?.fyTarget ?? 0;
  const fyTarget =
    carriedTarget > 0
      ? carriedTarget
      : (metric?.target_value ?? 0);

  // Headline cumulative = last actual point (= MAIN-17 FY total). Prefer the
  // series' total_value (the recompute writes the cumulative end there), then
  // the last bucket, then the MAIN-17 scalar.
  const lastCumulative =
    points[points.length - 1]?.cumulative ?? 0;
  const headlineValue =
    series.total_value ?? (lastCumulative || (metric?.value ?? 0));

  // quick-260630-f7f Task 6 (item 6): back Closed-Won out of the TARGET. When the
  // series carries an adjustedTarget (= fyTarget − Closed-Won FY booked), express
  // coverage against the REMAINING target so the ratio reflects pipeline-still-
  // needed. Falls back to the gross fyTarget for older cache rows (no field).
  const closedWonFy = points.find((p) => p.closedWonFy > 0)?.closedWonFy ?? 0;
  const carriedAdjusted = points.find((p) => p.adjustedTarget > 0)?.adjustedTarget ?? 0;
  const netOfClosedWon = carriedAdjusted > 0;
  // Effective coverage target: the remaining (net) target when available.
  const coverageTarget = netOfClosedWon ? carriedAdjusted : fyTarget;

  // The chip is "% OF TARGET" against the coverage target. e.g. open $4.75M /
  // remaining $X. Amber/down when < 100% (behind), green/up when >= 100%.
  const pctOfTarget =
    coverageTarget > 0 ? Math.round((headlineValue / coverageTarget) * 100) : null;
  const onTrack = pctOfTarget !== null && pctOfTarget >= 100;

  const months = points.map((p) => p.label);
  const cumulative = points.map((p) => p.cumulative);

  // The gap band + target line are drawn against the coverage (remaining) target.
  const option =
    points.length > 0
      ? buildEchartsOption('pacing', { months, cumulative, target: coverageTarget })
      : null;

  return (
    <Card data-testid={`main-tile-${metricId}`}>
      {header}

      {/* Value row — 38px headline + behind/ahead-of-pace chip. */}
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <p
          className="text-4xl font-bold leading-none tracking-tight tabular-nums text-ink-900"
          data-testid="main-kpi-value"
        >
          {formatCurrency(headlineValue)}
        </p>
        {pctOfTarget !== null && points.length > 0 && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${
              onTrack ? 'bg-good-bg text-good-text' : 'bg-warn-bg text-warn-text'
            }`}
            data-testid="pace-delta-chip"
          >
            <span aria-hidden>{onTrack ? '▲' : '▼'}</span> {pctOfTarget}% coverage
          </span>
        )}
      </div>

      {/* Context line — coverage target. When Closed-Won is backed out (item 6)
          show the REMAINING target + a "net of $X Closed-Won" breakdown. */}
      <p className="mt-2 text-xs text-ink-500" data-testid="pacing-context">
        open pipeline by close date · {netOfClosedWon ? 'remaining target' : 'target'}{' '}
        {formatCurrency(coverageTarget)} FY — coverage vs 3× quota
      </p>
      {netOfClosedWon && (
        <p className="mt-0.5 text-xs text-ink-400" data-testid="pacing-net-note">
          3× coverage on remaining quota · net of {formatCurrency(closedWonFy)} Closed-Won
          booked (gross 3× target {formatCurrency(fyTarget)})
        </p>
      )}

      {/* Empty state. */}
      {points.length === 0 && (
        <p
          className="py-12 text-center text-sm text-ink-500"
          data-testid="pacing-empty-state"
        >
          No open FY pipeline to pace.
        </p>
      )}

      {/* Pacing chart + legend. */}
      {option && (
        <>
          <div className="mt-3 h-[150px] w-full" data-testid="pacing-container">
            <ReactECharts
              option={option}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              notMerge
              lazyUpdate={false}
            />
          </div>
          {/* Legend — mirrors mock .pace-legend (actual line / target / gap). */}
          <div
            className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-500"
            data-testid="pace-legend"
          >
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-0 w-4 border-t-[2.5px] border-adeptia-cyan"
              />
              Cumulative actual
            </span>
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-0 w-4 border-t-2 border-dashed border-ink-600"
              />
              Target pace
            </span>
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-2.5 w-3.5 rounded-sm"
                style={{ backgroundColor: 'rgba(240,162,48,0.30)' }}
              />
              Gap to target
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
